import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box, Typography, Button, Chip, Avatar, TextField, CircularProgress,
  Stack, Divider, alpha, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetClassLiveDetailQuery, useCreateClassRequestMutation, useSendClassMessageMutation,
  useLazyGetMakeupEligibleStaffQuery, useCheckMakeupAvailabilityMutation,
  useRespondToClassRequestMutation,
} from "../../Api/attoDeskApi";
import { useNotifier } from "../../Core/Notifier";
import dayjs from "dayjs";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PlaceIcon from "@mui/icons-material/Place";
import LaptopIcon from "@mui/icons-material/Laptop";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { formatFromPrejoined } from "../../utils/classLabel";

const fmt12 = (t: string | undefined) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

// Muted premium palette (2026-08-25). Dark forest green removed as a
// hero identity; green is reserved for semantic Completed / Present.
// The rest of the file references these via helper functions below.
const pp = {
  navy:      "#17324D",
  navyStep:  "#31577F",
  blue:      "#4E78A6",
  steel:     "#6F96BE",
  indigo:    "#6878A6",
  surface:   "#F4F7FA",
  surfaceBlue: "#EAF2F8",
  surfaceGrey: "#F3F5F8",
  surfaceMint: "#F1F8F4",
  surfaceAmber:"#FBF7ED",
  surfaceRed:  "#FCF3F4",
  border:    "#E2E8F0",
  card:      "#FFFFFF",
  text:      "#17324D",
  value:     "#263F55",
  label:     "#6B7F92",
  muted:     "#7D8FA1",
  link:      "#557AA1",
  success:   "#5BAA83",
  successText:"#467F66",
  warning:   "#D6AE57",
  warningText:"#9B7A36",
  danger:    "#D97882",
  dangerText:"#B75F68",
  shadow:    "0 6px 18px rgba(23,50,77,0.05)",
  shadowHover:"0 10px 24px rgba(23,50,77,0.08)",
};

const statusConfig: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  upcoming:    { label: "Upcoming",    color: pp.blue,    bg: pp.surfaceBlue, icon: "🕐" },
  in_progress: { label: "In Progress", color: pp.warning, bg: pp.surfaceAmber, icon: "⏳" },
  live:        { label: "Live Now",    color: pp.danger,  bg: pp.surfaceRed,  icon: "🔴" },
  completed:   { label: "Completed",   color: pp.success, bg: pp.surfaceMint, icon: "✅" },
  cancelled:   { label: "Cancelled",   color: pp.muted,   bg: pp.surfaceGrey, icon: "❌" },
  scheduled:   { label: "Scheduled",   color: pp.blue,    bg: pp.surfaceBlue, icon: "📅" },
  missed:      { label: "Missed",      color: pp.danger,  bg: pp.surfaceRed,  icon: "⚠️" },
};

const preClassTemplates = [
  "Can we reschedule this class?",
  "My child will be absent today",
  "What topics will be covered?",
];
const postClassTemplates = [
  "Request makeup class",
  "Thank you for the class!",
  "How was my child's performance?",
];

const ParentClassDetail = () => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const { data, isLoading, refetch } = useGetClassLiveDetailQuery({ id: Number(id) }, { skip: !id, pollingInterval: 5000 });
  const [createRequest, { isLoading: isRequesting }] = useCreateClassRequestMutation();
  const [fetchEligibleStaff, { data: eligibleStaffResp }] = useLazyGetMakeupEligibleStaffQuery();
  const [checkAvailability, { data: availResp, isLoading: isCheckingAvail, reset: resetAvail }] = useCheckMakeupAvailabilityMutation();
  const [respondToRequest, { isLoading: isResponding }] = useRespondToClassRequestMutation();
  const eligibleStaff: { id: number; name: string }[] = ((eligibleStaffResp?.data as any) || []) as any;
  const availability: any = (availResp as any)?.data || null;
  const [sendMessage, { isLoading: isSending }] = useSendClassMessageMutation();

  const detail = useMemo(() => (data?.data as any) || null, [data]);
  const schedule = detail?.schedule;
  const st = statusConfig[detail?.displayStatus || "scheduled"] || statusConfig.scheduled;

  // Countdown
  const countdown = useMemo(() => {
    if (!schedule?.startTime || !schedule?.scheduledDate) return "";
    const now = dayjs();
    const [h, m] = schedule.startTime.split(":").map(Number);
    const classStart = dayjs(schedule.scheduledDate).hour(h).minute(m).second(0);
    const [eh, em] = (schedule.endTime || "00:00").split(":").map(Number);
    const classEnd = dayjs(schedule.scheduledDate).hour(eh).minute(em).second(0);

    if (detail?.displayStatus === "live" || detail?.displayStatus === "in_progress") {
      const left = classEnd.diff(now, "minute");
      return left > 0 ? `Ends in ${left}m` : "Ending soon";
    }
    if (now.isBefore(classStart)) {
      const diff = classStart.diff(now, "minute");
      if (diff > 1440) return `In ${Math.floor(diff / 1440)} day${Math.floor(diff / 1440) > 1 ? "s" : ""}`;
      if (diff > 60) return `Starts in ${Math.floor(diff / 60)}h ${diff % 60}m`;
      return `Starts in ${diff}m`;
    }
    return "";
  }, [schedule, detail?.displayStatus]);

  // Attendance result
  const attendanceResult = useMemo(() => {
    const records: any[] = detail?.attendance || [];
    if (records.length === 0) return null;
    return records[0]?.status || null;
  }, [detail?.attendance]);

  const [reqOpen, setReqOpen] = useState(false);
  const [reqType, setReqType] = useState<"absent" | "makeup">("absent");
  const [reqReason, setReqReason] = useState("");
  // Phase-6 makeup preferences — parents can supply a specific slot OR
  // opt into "I'm flexible" so Step Up staff pick the time. Everything
  // is a PREFERENCE; the parent never creates the actual makeup class.
  const [mkMode, setMkMode] = useState<"specific" | "flexible">("specific");
  const [mkDate, setMkDate] = useState("");
  const [mkStart, setMkStart] = useState("");
  const [mkEnd, setMkEnd] = useState("");
  const [mkStaff, setMkStaff] = useState<string>("");
  const [mkDays, setMkDays] = useState<string[]>([]);
  const [mkTimeOfDay, setMkTimeOfDay] = useState<string>("any");

  const resetReqState = () => {
    setReqReason("");
    setMkMode("specific");
    setMkDate(""); setMkStart(""); setMkEnd(""); setMkStaff("");
    setMkDays([]); setMkTimeOfDay("any");
    resetAvail();
  };

  // Fetch eligible staff when the makeup dialog opens. Backend re-checks
  // parent → student → occurrence ownership before returning anything.
  useEffect(() => {
    if (reqOpen && reqType === "makeup" && detail && (detail as any).studentId) {
      fetchEligibleStaff({
        student_id: (detail as any).studentId,
        class_schedule_id: (detail as any)?.id,
        class_occurrence_id: (detail as any)?.occurrenceId ?? (detail as any)?.occurrence_id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqOpen, reqType, (detail as any)?.id]);

  // Debounced availability check when the parent has a specific slot
  // filled in. Runs 500ms after the last change so it doesn't fire per
  // keystroke on the time inputs.
  useEffect(() => {
    if (!(reqOpen && reqType === "makeup" && mkMode === "specific" && detail && mkDate && mkStart && mkEnd && mkStart < mkEnd)) {
      return;
    }
    const t = setTimeout(() => {
      checkAvailability({
        student_id: (detail as any).studentId,
        class_schedule_id: (detail as any)?.id,
        class_occurrence_id: (detail as any)?.occurrenceId ?? (detail as any)?.occurrence_id,
        date: mkDate,
        start_time: mkStart,
        end_time: mkEnd,
        staff_id: mkStaff ? Number(mkStaff) : undefined,
      });
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqOpen, reqType, mkMode, mkDate, mkStart, mkEnd, mkStaff]);
  const [msgText, setMsgText] = useState("");
  const [optimisticMsgs, setOptimisticMsgs] = useState<any[]>([]);
  const msgEndRef = useRef<HTMLDivElement>(null);
  const msgContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  const allMessages = useMemo(() => {
    const real: any[] = detail?.messages || [];
    const optimisticIds = new Set(real.map((m: any) => m.message));
    const pending = optimisticMsgs.filter((m) => !optimisticIds.has(m.message));
    return [...real, ...pending];
  }, [detail?.messages, optimisticMsgs]);

  useEffect(() => {
    if (msgEndRef.current) {
      msgEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [allMessages.length]);

  // Clear optimistic messages once real data catches up
  useEffect(() => {
    if (optimisticMsgs.length > 0 && detail?.messages?.length) {
      const realTexts = new Set((detail.messages as any[]).map((m: any) => m.message));
      setOptimisticMsgs((prev) => prev.filter((m) => !realTexts.has(m.message)));
    }
  }, [detail?.messages]);

  // Context-aware templates
  const quickTemplates = useMemo(() => {
    if (detail?.displayStatus === "completed" || detail?.displayStatus === "missed") return postClassTemplates;
    return preClassTemplates;
  }, [detail?.displayStatus]);

  // Frontend validation: reason required (min 3 non-space chars) for
  // both request types. Makeup adds structured-slot validation when the
  // parent picks "specific" scheduling.
  const trimmedReason = reqReason.trim();
  const isReasonValid = trimmedReason.length >= 3;
  const isMakeupSlotValid = reqType !== "makeup"
    || mkMode === "flexible"
    || (mkDate && mkStart && mkEnd && mkStart < mkEnd);
  const canSubmit = isReasonValid && isMakeupSlotValid && !isRequesting;

  const handleSubmitRequest = async () => {
    if (!schedule || !canSubmit) return;
    const payload: any = {
      class_schedule_id: schedule.id,
      class_occurrence_id: (detail as any)?.occurrenceId ?? (detail as any)?.occurrence_id ?? schedule.id,
      student_id: schedule.studentId,
      request_type: reqType,
      reason: trimmedReason,
    };
    if (reqType === "makeup") {
      payload.scheduling_mode = mkMode;
      if (mkMode === "specific") {
        payload.requested_date       = mkDate;
        payload.requested_time       = mkStart;
        payload.requested_end_time   = mkEnd;
        if (mkStaff) payload.preferred_staff_id = Number(mkStaff);
      } else {
        if (mkDays.length > 0) payload.preferred_days = mkDays;
        if (mkTimeOfDay && mkTimeOfDay !== "any") payload.preferred_time_of_day = mkTimeOfDay;
        if (mkStaff) payload.preferred_staff_id = Number(mkStaff);
      }
    }
    try {
      const r = await createRequest(payload).unwrap();
      if (r.status) { showMessage(r.message); setReqOpen(false); resetReqState(); refetch(); }
      else showErrorMessage(r.message);
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  const handleSendMessage = async (text?: string) => {
    const msg = (text || msgText).trim();
    if (!msg || !schedule) return;
    // Optimistic append
    setOptimisticMsgs((prev) => [...prev, {
      id: `opt-${Date.now()}`, sender_type: "parent", message: msg,
      created_at: new Date().toISOString(), senderName: "You", _pending: true,
    }]);
    setMsgText("");
    try {
      const r = await sendMessage({ class_schedule_id: schedule.id, student_id: schedule.studentId, sender_type: "parent", message: msg }).unwrap();
      if (r.status) { refetch(); }
      else showErrorMessage(r.message);
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, gap: 2 }}>
        <CircularProgress size={32} />
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Loading class details...</Typography>
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box sx={{ p: 3, textAlign: "center", py: 8 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>Class not found</Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>The class you're looking for doesn't exist or couldn't be loaded.</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ textTransform: "none", borderRadius: "10px" }}>Go Back</Button>
      </Box>
    );
  }

  const isLive = detail.displayStatus === "live" || detail.displayStatus === "in_progress";
  const isUpcoming = detail.displayStatus === "upcoming" || detail.displayStatus === "scheduled";
  const isCompleted = detail.displayStatus === "completed";
  const isMissed = detail.displayStatus === "missed";
  const requests: any[] = detail.requests || [];
  const messages: any[] = detail.messages || [];

  const attColor = attendanceResult === "present" ? pp.success
    : attendanceResult === "late" ? pp.warning
    : attendanceResult === "absent" ? pp.danger
    : pp.muted;
  const attLabel = attendanceResult ? attendanceResult.charAt(0).toUpperCase() + attendanceResult.slice(1) : null;
  const attSurface = attendanceResult === "present" ? pp.surfaceMint
    : attendanceResult === "late" ? pp.surfaceAmber
    : attendanceResult === "absent" ? pp.surfaceRed
    : pp.surfaceBlue;
  const attTextColor = attendanceResult === "present" ? pp.successText
    : attendanceResult === "late" ? pp.warningText
    : attendanceResult === "absent" ? pp.dangerText
    : pp.muted;

  const classTitle = ((detail as any).className ?? formatFromPrejoined((detail as any).name, detail.gradeName, detail.subjectName)) || "Class";

  return (
    <Box sx={{
      pb: 3, minHeight: "100%",
      background: "linear-gradient(180deg, #F7F9FC 0%, #F3F6F9 100%)",
      mx: -3, mt: -3, px: 3, pt: 3,
    }}>
      {/* Ghost back button — muted navy pill, subtle blue hover. */}
      <Button
        startIcon={<ArrowBackIcon sx={{ transition: "transform 0.15s" }} />}
        onClick={() => navigate("/parent-portal/classes")}
        sx={{
          textTransform: "none", fontSize: 13, fontWeight: 700,
          mb: 2, color: pp.text,
          borderRadius: "10px", px: 1.5, py: 0.75,
          "&:hover": {
            bgcolor: pp.surfaceBlue, color: pp.blue,
            "& .MuiSvgIcon-root": { transform: "translateX(-2px)" },
          },
        }}
      >
        Back to Classes
      </Button>

      {/* ══════════ HERO — muted navy/steel-blue, always.
             Green appears only as the semantic Completed chip below,
             never as the hero background. Live gets a slim red accent
             on the completion strip via the same chip system. */}
      <Box sx={{
        background: `linear-gradient(135deg, ${pp.navy} 0%, ${pp.navyStep} 65%, ${pp.blue} 100%)`,
        borderRadius: "18px", px: { xs: 2.5, md: 3.5 }, py: { xs: 2.5, md: 3 },
        mb: 2.5, color: "#fff", position: "relative", overflow: "hidden",
        boxShadow: "0 12px 32px rgba(23,50,77,0.14)",
        "&::before": {
          content: '""', position: "absolute", top: -60, right: -60,
          width: 240, height: 240, borderRadius: "50%", pointerEvents: "none",
          background: "radial-gradient(circle at center, rgba(255,255,255,0.10) 0%, transparent 65%)",
        },
        "&::after": {
          content: '""', position: "absolute", bottom: -80, left: -40,
          width: 200, height: 200, borderRadius: "50%", pointerEvents: "none",
          background: `radial-gradient(circle at center, ${alpha(pp.steel, 0.30)} 0%, transparent 60%)`,
        },
      }}>
        <Box sx={{ position: "relative", zIndex: 1 }}>
          {/* Status + Countdown row — semantic chips only, no hero flood. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
            <Chip
              icon={
                isCompleted
                  ? <CheckCircleOutlineIcon sx={{ fontSize: "14px !important", color: "#D9F6E7 !important" }} />
                  : isLive
                  ? <FiberManualRecordIcon sx={{ fontSize: "10px !important", color: "#fff !important" }} />
                  : undefined
              }
              label={st.label}
              size="small"
              sx={{
                height: 26, fontSize: 11.5, fontWeight: 800, letterSpacing: "0.02em",
                bgcolor: isCompleted
                  ? "rgba(91,170,131,0.20)"
                  : isLive
                  ? "rgba(217,120,130,0.22)"
                  : "rgba(255,255,255,0.15)",
                color: isCompleted ? "#D9F6E7" : "#fff",
                border: isCompleted ? "1px solid rgba(91,170,131,0.35)" : "1px solid rgba(255,255,255,0.18)",
                "& .MuiChip-icon": { animation: isLive ? "pulse 1.5s infinite" : "none", "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } } },
              }}
            />
            {countdown && (
              <Chip
                icon={<TimerOutlinedIcon sx={{ fontSize: "13px !important", color: "#fff !important" }} />}
                label={countdown}
                size="small"
                sx={{ height: 26, fontSize: 11, fontWeight: 700, bgcolor: "rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.9)", border: "1px solid rgba(255,255,255,0.18)" }}
              />
            )}
          </Box>

          {/* Title */}
          <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
            {classTitle}
          </Typography>

          {/* Date + Time */}
          <Typography sx={{ fontSize: 13.5, opacity: 0.78, mt: 0.75 }}>
            {dayjs(schedule?.scheduledDate).format("dddd, MMMM D, YYYY")}
          </Typography>
          <Typography sx={{ fontSize: 17, fontWeight: 700, mt: 0.25 }}>
            {fmt12(schedule?.startTime)} – {fmt12(schedule?.endTime)}
          </Typography>

          {/* Teacher + Location + Mode inline meta */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1.75, flexWrap: "wrap" }}>
            {detail.staffName && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <PersonOutlineIcon sx={{ fontSize: 15, opacity: 0.7 }} />
                <Typography sx={{ fontSize: 13, opacity: 0.9, fontWeight: 600 }}>{detail.staffName}</Typography>
              </Box>
            )}
            {detail.locationName && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <PlaceIcon sx={{ fontSize: 15, opacity: 0.7 }} />
                <Typography sx={{ fontSize: 13, opacity: 0.9, fontWeight: 600 }}>{detail.locationName}</Typography>
              </Box>
            )}
            <Chip
              icon={detail.classMode === "Online"
                ? <LaptopIcon sx={{ fontSize: "13px !important", color: "#fff !important", opacity: 0.85 }} />
                : <PlaceIcon sx={{ fontSize: "13px !important", color: "#fff !important", opacity: 0.85 }} />}
              label={detail.classMode === "Online" ? "Online" : "In Person"}
              size="small"
              sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: "rgba(255,255,255,0.14)", color: "#fff", border: "1px solid rgba(255,255,255,0.18)" }}
            />
          </Box>

          {/* Student */}
          {detail.studentName && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.75, px: 1.5, py: 0.75, borderRadius: "10px", bgcolor: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.16)", width: "fit-content" }}>
              <ChildCareIcon sx={{ fontSize: 15, opacity: 0.75 }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{detail.studentName}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 380px" }, gap: 2.5 }}>
        {/* ══════════ LEFT COLUMN ══════════ */}
        <Stack spacing={2.5}>

          {/* ── Attendance ── */}
          {(isCompleted || isMissed) && (
            <Box sx={{
              bgcolor: pp.card, borderRadius: "16px",
              border: `1px solid ${pp.border}`, boxShadow: pp.shadow,
              p: 2.5,
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: "10px",
                  bgcolor: pp.surfaceBlue, color: pp.blue,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: pp.text, letterSpacing: "-0.01em" }}>
                  Attendance
                </Typography>
              </Box>
              {attendanceResult ? (
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1.5,
                  p: 1.75, borderRadius: "12px",
                  bgcolor: attSurface,
                  border: `1px solid ${alpha(attColor, 0.24)}`,
                }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: attColor, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 10px ${alpha(attColor, 0.28)}` }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: attTextColor }}>{attLabel}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: pp.muted }}>
                      {attendanceResult === "present" ? "Your child attended this class"
                        : attendanceResult === "late" ? "Your child arrived late"
                        : "Your child was absent"}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                // Polished "not marked" state — pale blue panel, no giant grey bar.
                <Box sx={{
                  display: "flex", alignItems: "center", gap: 1.5,
                  p: 1.75, borderRadius: "12px",
                  bgcolor: pp.surfaceBlue, border: `1px solid ${pp.border}`,
                }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: "12px", bgcolor: pp.card, color: pp.blue, border: `1px solid ${pp.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <TimerOutlinedIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: pp.text }}>Attendance not yet recorded</Typography>
                    <Typography sx={{ fontSize: 11.5, color: pp.muted, mt: 0.15 }}>
                      Your child's attendance for this class has not been submitted yet.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* ── Class Information — 2-col info grid, blue icon tiles. */}
          <Box sx={{
            bgcolor: pp.card, borderRadius: "16px",
            border: `1px solid ${pp.border}`, boxShadow: pp.shadow,
            p: 2.5,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: "10px",
                bgcolor: pp.surfaceBlue, color: pp.blue,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <SchoolOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: pp.text, letterSpacing: "-0.01em" }}>
                Class Information
              </Typography>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
              {[
                { icon: <AccessTimeIcon sx={{ fontSize: 18 }} />,    label: "Time",     value: `${fmt12(schedule?.startTime)} – ${fmt12(schedule?.endTime)}`, tint: pp.steel  },
                { icon: <PersonOutlineIcon sx={{ fontSize: 18 }} />, label: "Teacher",  value: detail.staffName,                                                tint: pp.indigo },
                { icon: <SchoolOutlinedIcon sx={{ fontSize: 18 }} />,label: "Grade",    value: detail.gradeName,                                                tint: pp.blue   },
                { icon: <PlaceIcon sx={{ fontSize: 18 }} />,         label: "Location", value: detail.locationName || "Not specified",                          tint: pp.steel  },
                { icon: <LaptopIcon sx={{ fontSize: 18 }} />,        label: "Mode",     value: detail.classMode === "Online" ? "Online" : "In Person",          tint: pp.indigo },
                { icon: <ChildCareIcon sx={{ fontSize: 18 }} />,     label: "Student",  value: detail.studentName,                                              tint: pp.navy   },
              ].map((r, i) => (
                <Box key={i} sx={{
                  display: "flex", alignItems: "center", gap: 1.5,
                  p: 1.25, borderRadius: "12px",
                  bgcolor: pp.card, border: `1px solid ${pp.border}`,
                  transition: "border-color 0.15s, box-shadow 0.15s",
                  "&:hover": { borderColor: alpha(r.tint, 0.35) },
                }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: "10px",
                    bgcolor: alpha(r.tint, 0.10), color: r.tint,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>{r.icon}</Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: pp.label, textTransform: "uppercase", letterSpacing: "0.06em" }}>{r.label}</Typography>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: pp.value, mt: 0.15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.value || "—"}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── Actions / status card. Header uses a semantic icon
                 tile that flips color with the class state. */}
          <Box sx={{
            bgcolor: pp.card, borderRadius: "16px",
            border: `1px solid ${pp.border}`, boxShadow: pp.shadow,
            p: 2.5,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: isUpcoming ? 2 : 1.5 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: "10px",
                bgcolor: isCompleted ? pp.surfaceMint : isLive ? pp.surfaceRed : pp.surfaceBlue,
                color: isCompleted ? pp.success : isLive ? pp.danger : pp.blue,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {isCompleted ? <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                  : isLive ? <FiberManualRecordIcon sx={{ fontSize: 14 }} />
                  : <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />}
              </Box>
              <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: pp.text, letterSpacing: "-0.01em" }}>
                {isLive ? "Class is Live" : isCompleted ? "Class Completed" : "Actions"}
              </Typography>
            </Box>
            {isUpcoming && (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                {(() => {
                  const ACTIVE = ["pending", "alternative_proposed", "approved"];
                  const hasActiveAbsence = requests.some((r: any) => r.request_type === "absent" && ACTIVE.includes(r.status));
                  const hasActiveMakeup  = requests.some((r: any) => r.request_type === "makeup" && ACTIVE.includes(r.status));
                  return (
                    <>
                      {hasActiveAbsence ? (
                        <Button fullWidth variant="outlined" disabled startIcon={<EventBusyOutlinedIcon />}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "12px", py: 1.25 }}>
                          Absence Request Submitted
                        </Button>
                      ) : (
                        <Button fullWidth variant="outlined" startIcon={<EventBusyOutlinedIcon />}
                          onClick={() => { setReqType("absent"); resetReqState(); setReqOpen(true); }}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "12px", py: 1.25, borderColor: alpha("#ef4444", 0.3), color: "#ef4444", "&:hover": { bgcolor: alpha("#ef4444", 0.04) } }}>
                          Request Absence
                        </Button>
                      )}
                      {hasActiveMakeup ? (
                        <Button fullWidth variant="outlined" disabled startIcon={<ReplayIcon />}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "12px", py: 1.25 }}>
                          Makeup Request Submitted
                        </Button>
                      ) : (
                        <Button fullWidth variant="outlined" startIcon={<ReplayIcon />}
                          onClick={() => { setReqType("makeup"); resetReqState(); setReqOpen(true); }}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "12px", py: 1.25, borderColor: alpha("#f59e0b", 0.3), color: "#b45309", "&:hover": { bgcolor: alpha("#f59e0b", 0.04) } }}>
                          Request Makeup Class
                        </Button>
                      )}
                      <Button fullWidth variant="outlined" startIcon={<ChatBubbleOutlineIcon />}
                        onClick={() => {
                          // Manual message composer lives on the Parent
                          // Portal Messages page. Passing the class +
                          // teacher context via URL params so the
                          // composer opens pre-filled with:
                          //   - Regarding: this child
                          //   - To: this class's assigned teacher
                          //   - Class: label for the context block
                          // The composer re-verifies eligibility on the
                          // server (parent→student→staff), so passing
                          // ids through the URL is safe.
                          const q = new URLSearchParams({
                            compose: "1",
                            ...(schedule?.studentId ? { studentId: String(schedule.studentId) } : {}),
                            ...((detail as any)?.occurrenceId ? { occurrenceId: String((detail as any).occurrenceId) } : {}),
                            ...(schedule?.id ? { scheduleId: String(schedule.id) } : {}),
                            ...(detail.staffUserId ? { staffUserId: String(detail.staffUserId) } : {}),
                            ...((detail as any)?.className || (detail.gradeName || detail.subjectName)
                              ? { classLabel: ((detail as any).className ?? formatFromPrejoined((detail as any).name, detail.gradeName, detail.subjectName)) || "" }
                              : {}),
                          });
                          navigate(`/parent-portal/messages?${q.toString()}`);
                        }}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: "12px", py: 1.25, borderColor: alpha("#0ea5e9", 0.3), color: "#0ea5e9", "&:hover": { bgcolor: alpha("#0ea5e9", 0.04) } }}>
                        Message Teacher
                      </Button>
                    </>
                  );
                })()}
              </Stack>
            )}
            {isLive && (
              <Box sx={{ p: 2, borderRadius: "12px", bgcolor: alpha("#ef4444", 0.04), border: `1px solid ${alpha("#ef4444", 0.1)}`, display: "flex", alignItems: "center", gap: 1.5 }}>
                <FiberManualRecordIcon sx={{ fontSize: 12, color: "#ef4444", animation: "pulse 1.5s infinite", "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } } }} />
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#dc2626" }}>Class is happening now</Typography>
                  <Typography sx={{ fontSize: 12, color: "#991b1b" }}>Use the messages panel to communicate with the teacher</Typography>
                </Box>
              </Box>
            )}
            {(isCompleted || isMissed) && attendanceResult === "absent" && (
              <Button fullWidth variant="outlined" startIcon={<ReplayIcon />}
                onClick={() => { setReqType("makeup"); resetReqState(); setReqOpen(true); }}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: "12px", py: 1.25, borderColor: alpha("#f59e0b", 0.3), color: "#b45309" }}>
                Request Makeup Class
              </Button>
            )}
            {isCompleted && attendanceResult !== "absent" && (
              <Box sx={{
                p: 1.75, borderRadius: "12px",
                bgcolor: pp.surfaceMint,
                border: `1px solid ${alpha(pp.success, 0.25)}`,
                display: "flex", alignItems: "center", gap: 1.5,
              }}>
                <Box sx={{ width: 34, height: 34, borderRadius: "10px", bgcolor: pp.success, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 10px ${alpha(pp.success, 0.28)}`, flexShrink: 0 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, color: pp.successText, fontWeight: 800 }}>This class has been completed.</Typography>
                  <Typography sx={{ fontSize: 11.5, color: pp.muted, mt: 0.15 }}>Thank you!</Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* ── Requests ── */}
          {requests.length > 0 && (
            <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: "1px solid rgba(0,0,0,0.05)", p: 2.5 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 2 }}>Your Requests</Typography>
              <Stack spacing={1.25}>
                {requests.map((req: any) => {
                  const statusMap: Record<string, { color: string; label: string }> = {
                    pending:              { color: "#f59e0b", label: "Pending Review" },
                    alternative_proposed: { color: "#8b5cf6", label: "Alternative Proposed" },
                    approved:             { color: "#22c55e", label: "Approved" },
                    scheduled:            { color: "#22c55e", label: "Scheduled" },
                    rejected:             { color: "#ef4444", label: "Rejected" },
                    cancelled:            { color: "#6b7280", label: "Cancelled" },
                  };
                  const chip = statusMap[req.status] || { color: "#6b7280", label: req.status };
                  const isMakeup = req.request_type === "makeup";
                  const isProposed = req.status === "alternative_proposed";
                  const isScheduled = req.status === "scheduled" && req.final_makeup_occurrence_id;
                  return (
                    <Box key={req.id} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${alpha(chip.color, 0.12)}`, bgcolor: alpha(chip.color, 0.02) }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                          {isMakeup ? "Makeup" : req.request_type === "absent" ? "Absence" : req.request_type} Request
                        </Typography>
                        <Chip label={chip.label} size="small" sx={{ height: 20, fontSize: 9.5, fontWeight: 700, bgcolor: alpha(chip.color, 0.1), color: chip.color }} />
                      </Box>
                      {req.reason && <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>{req.reason}</Typography>}
                      <Typography sx={{ fontSize: 10, color: "text.disabled", mt: 0.5 }}>
                        Submitted {dayjs(req.created_at).format("MMM D, YYYY · h:mm A")}
                      </Typography>

                      {/* Makeup: parent's original preference */}
                      {isMakeup && req.scheduling_mode === "specific" && req.requested_date && (
                        <Box sx={{ mt: 1, pl: 1, borderLeft: `2px solid ${alpha("#f59e0b", 0.3)}` }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>Your Preference</Typography>
                          <Typography sx={{ fontSize: 12 }}>
                            {dayjs(req.requested_date).format("ddd, MMM D, YYYY")} · {fmt12(req.requested_time)} – {fmt12(req.requested_end_time)}
                          </Typography>
                        </Box>
                      )}
                      {isMakeup && req.scheduling_mode === "flexible" && (
                        <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5, fontStyle: "italic" }}>
                          Flexible — Step Up will arrange a suitable time.
                        </Typography>
                      )}

                      {/* Staff proposal block */}
                      {isProposed && (
                        <Box sx={{ mt: 1.25, p: 1.25, borderRadius: "10px", bgcolor: alpha("#8b5cf6", 0.06), border: `1px solid ${alpha("#8b5cf6", 0.15)}` }}>
                          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#6d28d9", textTransform: "uppercase", mb: 0.5 }}>
                            Alternative Makeup Time Proposed
                          </Typography>
                          {req.proposed_date && (
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                              {dayjs(req.proposed_date).format("dddd, MMMM D, YYYY")}
                            </Typography>
                          )}
                          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                            {fmt12(req.proposed_start_time)} – {fmt12(req.proposed_end_time)}
                            {req.proposed_staff_name ? ` · Teacher: ${req.proposed_staff_name}` : ""}
                            {req.proposed_mode ? ` · ${req.proposed_mode}` : ""}
                          </Typography>
                          {req.proposal_note && (
                            <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5, fontStyle: "italic" }}>Staff note: {req.proposal_note}</Typography>
                          )}
                          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                            <Button
                              size="small" variant="contained"
                              disabled={isResponding}
                              onClick={async () => {
                                try {
                                  const r = await respondToRequest({ id: req.id, action: "accept" }).unwrap();
                                  if (r.status) { showMessage(r.message); refetch(); } else { showErrorMessage(r.message); }
                                } catch (e: any) { showErrorMessage(e?.data?.message || "Failed to respond"); }
                              }}
                              sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 700, borderRadius: "8px", bgcolor: "#22c55e", "&:hover": { bgcolor: "#16a34a" } }}
                            >
                              Accept Proposed Time
                            </Button>
                            <Button
                              size="small" variant="outlined"
                              disabled={isResponding}
                              onClick={async () => {
                                try {
                                  const r = await respondToRequest({ id: req.id, action: "request_another" }).unwrap();
                                  if (r.status) { showMessage(r.message); refetch(); } else { showErrorMessage(r.message); }
                                } catch (e: any) { showErrorMessage(e?.data?.message || "Failed to respond"); }
                              }}
                              sx={{ textTransform: "none", fontSize: 11.5, fontWeight: 700, borderRadius: "8px" }}
                            >
                              Request Another Time
                            </Button>
                          </Box>
                        </Box>
                      )}

                      {/* Final scheduled block */}
                      {isScheduled && (
                        <Box sx={{ mt: 1.25, p: 1.25, borderRadius: "10px", bgcolor: alpha("#22c55e", 0.06), border: `1px solid ${alpha("#22c55e", 0.15)}` }}>
                          <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#15803d", textTransform: "uppercase", mb: 0.5 }}>
                            Makeup Scheduled
                          </Typography>
                          <Typography sx={{ fontSize: 12 }}>
                            {req.proposed_date ? dayjs(req.proposed_date).format("dddd, MMMM D, YYYY") : ""}
                            {req.proposed_start_time ? ` · ${fmt12(req.proposed_start_time)} – ${fmt12(req.proposed_end_time)}` : ""}
                          </Typography>
                        </Box>
                      )}

                      {req.admin_notes && (
                        <Typography sx={{ fontSize: 11, color: chip.color, mt: 0.75, fontStyle: "italic" }}>
                          Staff note: {req.admin_notes}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Stack>

        {/* ══════════ RIGHT: MESSAGES — premium communication card. */}
        <Box sx={{
          bgcolor: pp.card, borderRadius: "16px",
          border: `1px solid ${pp.border}`, boxShadow: pp.shadow,
          display: "flex", flexDirection: "column",
          height: "fit-content", maxHeight: 620,
        }}>
          {/* Header with class context */}
          <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${pp.border}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: "10px",
                bgcolor: pp.surfaceBlue, color: pp.blue,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: pp.text, letterSpacing: "-0.01em", flex: 1 }}>Messages</Typography>
              {allMessages.length > 0 && (
                <Chip label={allMessages.length} size="small"
                  sx={{ height: 22, fontSize: 11, fontWeight: 800, bgcolor: pp.surfaceBlue, color: pp.blue, border: `1px solid ${pp.border}` }} />
              )}
            </Box>
            <Box sx={{
              mt: 1.25, p: 1.25, borderRadius: "10px",
              bgcolor: pp.surfaceBlue, border: `1px solid ${pp.border}`,
            }}>
              <Typography sx={{ fontSize: 12, color: pp.text, fontWeight: 800 }}>
                {classTitle}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: pp.muted, mt: 0.25 }}>
                {dayjs(schedule?.scheduledDate).format("ddd, MMM D")} · {fmt12(schedule?.startTime)} – {fmt12(schedule?.endTime)}
              </Typography>
              {detail.staffName && (
                <Typography sx={{ fontSize: 10.5, color: pp.label, mt: 0.15 }}>
                  Teacher: <Box component="span" sx={{ fontWeight: 800, color: pp.value }}>{detail.staffName}</Box>
                </Typography>
              )}
            </Box>
          </Box>

          {/* Messages body */}
          <Box ref={msgContainerRef} sx={{
            flex: 1, overflowY: "auto",
            px: 2.5, py: 2, minHeight: 220,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": { bgcolor: alpha(pp.steel, 0.24), borderRadius: 3 },
          }}>
            {allMessages.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 2.5 }}>
                <Box sx={{
                  width: 56, height: 56, borderRadius: "16px",
                  bgcolor: pp.surfaceBlue, color: pp.blue,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  mx: "auto", mb: 1.5,
                }}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 26 }} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 0.35, color: pp.text }}>Start a conversation</Typography>
                <Typography sx={{ fontSize: 12, color: pp.muted, mb: 2, maxWidth: 260, mx: "auto" }}>
                  Message {detail.staffName || "the teacher"} about this class.
                </Typography>
                <Stack spacing={0.75}>
                  {quickTemplates.map((qm, i) => (
                    <Box key={i} onClick={() => handleSendMessage(qm)}
                      role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleSendMessage(qm); } }}
                      sx={{
                        px: 1.5, py: 1.15, borderRadius: "12px",
                        border: `1px solid ${pp.border}`, bgcolor: pp.card,
                        cursor: "pointer", fontSize: 12.5, color: pp.value, textAlign: "left",
                        display: "flex", alignItems: "center", gap: 1,
                        fontWeight: 700,
                        transition: "all 0.15s",
                        outline: "none",
                        "&:hover": { bgcolor: "#F2F6FA", borderColor: pp.steel, color: pp.blue },
                        "&:focus-visible": { outline: `2px solid ${pp.blue}`, outlineOffset: 2 },
                      }}>
                      <SendIcon sx={{ fontSize: 13, color: pp.steel }} />
                      {qm}
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : (
              <Stack spacing={0.5}>
                {allMessages.map((msg: any, i: number) => {
                  const isMe = msg.sender_type === "parent";
                  const prev = i > 0 ? allMessages[i - 1] : null;
                  const sameSenderAsPrev = prev && prev.sender_type === msg.sender_type;
                  const isPending = msg._pending;

                  return (
                    <Box key={msg.id}>
                      {!isMe && !sameSenderAsPrev && (
                        <Typography sx={{ fontSize: 10, fontWeight: 800, color: pp.blue, mb: 0.5, mt: i > 0 ? 1.5 : 0, ml: 1, letterSpacing: "0.02em" }}>
                          {msg.senderName || "Teacher"}
                        </Typography>
                      )}
                      {isMe && !sameSenderAsPrev && i > 0 && <Box sx={{ mt: 1.5 }} />}
                      <Box sx={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start" }}>
                        <Box sx={{
                          maxWidth: "82%",
                          bgcolor: isMe ? pp.blue : pp.surfaceBlue,
                          color: isMe ? "#fff" : pp.text,
                          border: isMe ? "none" : `1px solid ${pp.border}`,
                          borderRadius: isMe
                            ? sameSenderAsPrev ? "14px 4px 4px 14px" : "14px 14px 4px 14px"
                            : sameSenderAsPrev ? "4px 14px 14px 4px" : "14px 14px 14px 4px",
                          px: 1.75, py: 1,
                          opacity: isPending ? 0.7 : 1,
                          transition: "opacity 0.3s",
                          boxShadow: isMe ? `0 4px 10px ${alpha(pp.blue, 0.20)}` : "none",
                        }}>
                          <Typography sx={{ fontSize: 13, lineHeight: 1.45, fontWeight: isMe ? 600 : 500 }}>{msg.message}</Typography>
                          <Typography sx={{ fontSize: 9, mt: 0.25, opacity: isMe ? 0.7 : 0.55, textAlign: isMe ? "right" : "left", fontWeight: 600 }}>
                            {isPending ? "Sending..." : dayjs(msg.created_at).format("h:mm A")}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
                <div ref={msgEndRef} />
              </Stack>
            )}
          </Box>

          {/* Quick templates chip strip — shown while there's an active convo. */}
          {allMessages.length > 0 && (
            <Box sx={{
              px: 2, py: 1, borderTop: `1px solid ${pp.border}`,
              display: "flex", gap: 0.75, overflowX: "auto",
              "&::-webkit-scrollbar": { height: 0 },
            }}>
              {quickTemplates.slice(0, 3).map((qm, i) => (
                <Chip key={i} label={qm} size="small" onClick={() => handleSendMessage(qm)}
                  sx={{
                    height: 26, fontSize: 11, fontWeight: 700,
                    cursor: "pointer", flexShrink: 0,
                    bgcolor: pp.card, color: pp.value,
                    border: `1px solid ${pp.border}`,
                    "&:hover": { bgcolor: "#F2F6FA", borderColor: pp.steel, color: pp.blue },
                  }} />
              ))}
            </Box>
          )}

          {/* Composer */}
          <Box sx={{
            px: 2, py: 1.5, borderTop: `1px solid ${pp.border}`,
            display: "flex", gap: 1, alignItems: "flex-end",
            bgcolor: "#FAFCFE",
          }}>
            <TextField id="msg-input" size="small" fullWidth
              placeholder={`Message ${detail.staffName || "teacher"}...`}
              value={msgText} onChange={(e) => setMsgText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px", fontSize: 13, bgcolor: pp.card,
                  "& fieldset": { borderColor: pp.border },
                  "&:hover fieldset": { borderColor: pp.steel },
                  "&.Mui-focused fieldset": { borderColor: pp.blue, borderWidth: "2px" },
                },
              }} />
            <Button variant="contained"
              onClick={() => handleSendMessage()}
              disabled={!msgText.trim() || isSending}
              sx={{
                width: 42, minWidth: 42, height: 40,
                borderRadius: "12px", boxShadow: "none", p: 0,
                bgcolor: pp.blue,
                "&:hover": { bgcolor: pp.navyStep, boxShadow: `0 6px 14px ${alpha(pp.blue, 0.30)}` },
                "&.Mui-disabled": { bgcolor: alpha(pp.steel, 0.20), color: "#fff" },
              }}>
              <SendIcon sx={{ fontSize: 18 }} />
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ══ Request Dialog — Phase-6 structured request workflow ══
          Absence: reason REQUIRED, no attendance mutation server-side.
          Makeup: reason REQUIRED + specific-slot OR flexible mode.
          Parents never create attendance records or makeup occurrences
          directly; staff review each request in StaffLiveClass. */}
      <Dialog
        open={reqOpen}
        onClose={() => { setReqOpen(false); resetReqState(); }}
        maxWidth={reqType === "makeup" ? "sm" : "xs"}
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
          {reqType === "absent" ? "Request Absence" : "Request Makeup Class"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2 }}>
            {reqType === "absent"
              ? (detail.staffName
                  ? `Send an absence request to ${detail.staffName} for ${detail.studentName || "your child"}. The teacher or an authorized Step Up staff member will review your request.`
                  : `Send an absence request for ${detail.studentName || "your child"}. An authorized Step Up staff member will review your request.`)
              : `Tell us your preferred makeup schedule. Step Up staff will review availability and confirm the final class.`}
          </Typography>

          {/* Class summary + destination block. Same shape for absence and
              makeup so the parent always sees WHAT class and WHO the
              request goes to before submitting. Data source is the same
              class-live detail payload the page header already uses —
              never a second inconsistent teacher lookup. */}
          <Box sx={{
            mb: 2, p: 1.5, borderRadius: "10px",
            bgcolor: reqType === "absent" ? alpha("#0ea5e9", 0.05) : alpha("#f59e0b", 0.05),
            border: `1px solid ${reqType === "absent" ? alpha("#0ea5e9", 0.15) : alpha("#f59e0b", 0.15)}`,
          }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
              {reqType === "absent" ? "Class" : "Original Class"}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
              {((detail as any).className ?? formatFromPrejoined((detail as any).name, detail.gradeName, detail.subjectName)) || "Class"}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.25 }}>
              {schedule?.scheduledDate ? dayjs(schedule.scheduledDate).format("dddd, MMMM D, YYYY") : ""}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
              {schedule?.startTime ? fmt12(schedule.startTime) : ""}
              {schedule?.endTime ? ` – ${fmt12(schedule.endTime)}` : ""}
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
              {reqType === "absent" ? "Sending to" : "Original Teacher"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <PersonOutlineIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {detail.staffName || "Step Up Scheduling Team"}
              </Typography>
            </Box>
            {reqType === "absent" && (
              <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.5 }}>
                Your request will also be visible to authorized Step Up scheduling staff.
              </Typography>
            )}
          </Box>

          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>
            {reqType === "absent" ? "Reason for absence *" : "Reason for makeup *"}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            placeholder={reqType === "absent"
              ? "e.g. My child is sick today, Medical appointment, Family emergency…"
              : "e.g. Missed due to illness, unable to attend original class…"}
            value={reqReason}
            onChange={(e) => setReqReason(e.target.value)}
            error={reqReason.length > 0 && !isReasonValid}
            helperText={reqReason.length > 0 && !isReasonValid ? "Please write at least 3 characters." : " "}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />

          {reqType === "makeup" && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 1 }}>
                Scheduling Preference *
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1.5 }}>
                {[
                  { key: "specific", label: "I have a preferred date and time" },
                  { key: "flexible", label: "I'm flexible — Step Up can arrange a suitable time" },
                ].map((opt) => {
                  const active = mkMode === opt.key;
                  return (
                    <Box
                      key={opt.key}
                      onClick={() => setMkMode(opt.key as any)}
                      sx={{
                        px: 1.5, py: 1, borderRadius: "10px", cursor: "pointer",
                        border: `1.5px solid ${active ? "#f59e0b" : "rgba(0,0,0,0.1)"}`,
                        bgcolor: active ? alpha("#f59e0b", 0.06) : "transparent",
                        display: "flex", alignItems: "center", gap: 1,
                      }}
                    >
                      <Box sx={{
                        width: 16, height: 16, borderRadius: "50%",
                        border: `2px solid ${active ? "#f59e0b" : "rgba(0,0,0,0.25)"}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        {active && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#f59e0b" }} />}
                      </Box>
                      <Typography sx={{ fontSize: 12.5, fontWeight: active ? 700 : 500 }}>{opt.label}</Typography>
                    </Box>
                  );
                })}
              </Box>

              {mkMode === "specific" ? (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.25 }}>Preferred Date *</Typography>
                    <TextField
                      type="date" size="small" fullWidth
                      value={mkDate}
                      onChange={(e) => setMkDate(e.target.value)}
                      inputProps={{ min: dayjs().add(1, "day").format("YYYY-MM-DD") }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.25 }}>Start Time *</Typography>
                    <TextField type="time" size="small" fullWidth value={mkStart} onChange={(e) => setMkStart(e.target.value)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.25 }}>End Time *</Typography>
                    <TextField type="time" size="small" fullWidth value={mkEnd} onChange={(e) => setMkEnd(e.target.value)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
                  </Box>
                  <Box sx={{ gridColumn: { xs: "1", sm: "1 / span 2" } }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.25 }}>Preferred Teacher (optional)</Typography>
                    <TextField
                      select size="small" fullWidth
                      SelectProps={{ native: true }}
                      value={mkStaff}
                      onChange={(e) => setMkStaff(e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    >
                      <option value="">No preference</option>
                      {eligibleStaff.map((s: any) => (
                        <option key={s.id} value={String(s.id)}>{s.name}</option>
                      ))}
                    </TextField>
                  </Box>
                  {/* Availability indicator. Backend uses the same canonical
                      conflict helpers the admin scheduler uses. Never
                      reveals what the conflicting staff/student is doing. */}
                  {mkDate && mkStart && mkEnd && mkStart < mkEnd && (
                    <Box sx={{ gridColumn: "1 / -1" }}>
                      {isCheckingAvail ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 11.5, color: "text.secondary" }}>
                          <CircularProgress size={12} /> Checking availability…
                        </Box>
                      ) : availability ? (
                        <Box sx={{
                          display: "flex", alignItems: "center", gap: 1,
                          p: 1, borderRadius: "8px",
                          bgcolor: availability.requested_slot_available ? alpha("#22c55e", 0.06) : alpha("#ef4444", 0.06),
                          border: `1px solid ${availability.requested_slot_available ? alpha("#22c55e", 0.2) : alpha("#ef4444", 0.2)}`,
                        }}>
                          {availability.requested_slot_available
                            ? <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                            : <EventBusyOutlinedIcon sx={{ fontSize: 16, color: "#dc2626" }} />}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: availability.requested_slot_available ? "#15803d" : "#b91c1c" }}>
                              {availability.requested_slot_available ? "This preferred time appears available." : "This time is not currently available."}
                            </Typography>
                            {!availability.requested_slot_available && (
                              <Typography sx={{ fontSize: 10.5, color: "text.secondary", mt: 0.25 }}>
                                {availability.message} You can pick another time, another teacher, or switch to "I'm flexible".
                              </Typography>
                            )}
                          </Box>
                          {!availability.requested_slot_available && (
                            <Button size="small" onClick={() => setMkMode("flexible")}
                              sx={{ ml: "auto", textTransform: "none", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                              Switch to Flexible
                            </Button>
                          )}
                        </Box>
                      ) : null}
                    </Box>
                  )}
                  {mkStart && mkEnd && mkStart >= mkEnd && (
                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography sx={{ fontSize: 11, color: "#dc2626" }}>End time must be after start time.</Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>Preferred Days (optional)</Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => {
                        const active = mkDays.includes(d);
                        return (
                          <Chip
                            key={d}
                            label={d}
                            size="small"
                            clickable
                            onClick={() => setMkDays(active ? mkDays.filter((x) => x !== d) : [...mkDays, d])}
                            sx={{
                              height: 26, fontSize: 11, fontWeight: 600, borderRadius: "8px",
                              bgcolor: active ? "#f59e0b" : "rgba(0,0,0,0.04)",
                              color: active ? "#fff" : "text.secondary",
                              "&:hover": { bgcolor: active ? "#d97706" : "rgba(0,0,0,0.08)" },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>Preferred Time of Day (optional)</Typography>
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                      {[
                        { key: "morning",   label: "Morning" },
                        { key: "afternoon", label: "Afternoon" },
                        { key: "evening",   label: "Evening" },
                        { key: "any",       label: "Any Time" },
                      ].map((t) => {
                        const active = mkTimeOfDay === t.key;
                        return (
                          <Chip
                            key={t.key}
                            label={t.label}
                            size="small"
                            clickable
                            onClick={() => setMkTimeOfDay(t.key)}
                            sx={{
                              height: 26, fontSize: 11, fontWeight: 600, borderRadius: "8px",
                              bgcolor: active ? "#f59e0b" : "rgba(0,0,0,0.04)",
                              color: active ? "#fff" : "text.secondary",
                              "&:hover": { bgcolor: active ? "#d97706" : "rgba(0,0,0,0.08)" },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              )}

              <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 1.5, fontStyle: "italic" }}>
                This is a preference only. Step Up staff must confirm the final makeup schedule.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => { setReqOpen(false); resetReqState(); }} sx={{ textTransform: "none", borderRadius: "10px" }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitRequest}
            disabled={!canSubmit}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 3, boxShadow: "none" }}
          >
            {isRequesting ? <CircularProgress size={16} color="inherit" /> : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParentClassDetail;
